import React from 'react';
import * as d3 from 'd3';
import { DataPoint, SeriesInfo, FitResponse } from '../../../types/chart';

export interface BaseChartProps {
  data: DataPoint[];
  seriesList: string[];
  seriesInfo: Record<string, SeriesInfo>;
  seriesVisibility: Record<string, boolean>;
  xAxisLabel: string;
  yAxisLabel: string;
  xLogScale: boolean;
  yLogScale: boolean;
  xMinValue: number;
  xMaxValue: number;
  yMinValue: number;
  yMaxValue: number;
  fitResults?: Record<string, FitResponse | null>;
  onSeriesClick?: (series: string) => void;
  onVisibilityToggle?: (series: string) => void;
}

// 导出形状类型映射，方便其他图表组件使用
export const shapeMap: Record<string, any> = {
  circle: d3.symbolCircle,
  triangle: d3.symbolTriangle,
  square: d3.symbolSquare,
  diamond: d3.symbolDiamond,
  cross: d3.symbolCross,
  star: d3.symbolStar
};

const BaseChart: React.FC<BaseChartProps> = ({
  data,
  seriesList,
  seriesInfo,
  seriesVisibility,
  xAxisLabel,
  yAxisLabel,
  xLogScale,
  yLogScale,
  xMinValue,
  xMaxValue,
  yMinValue,
  yMaxValue,
  onSeriesClick,
  onVisibilityToggle
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // 创建图表的基础部分，子类会重写或扩展这部分
    if (!chartRef.current || data.length === 0) return;
    
    // 清除之前的图表
    d3.select(chartRef.current).selectAll("*").remove();
    
    // 根据是否为对数坐标轴过滤数据
    const plotData = xLogScale ? data.filter(d => typeof d.x === 'number' && d.x > 0) : data;

    if (plotData.length === 0) {
      d3.select(chartRef.current).append('div').text('没有可显示的数据。').style('text-align', 'center').style('margin-top', '50px');
      return;
    }
    
    const margin = { top: 20, right: 200, bottom: 60, left: 60 };
    const width = 700 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
    
    // 创建SVG元素
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // 设置坐标轴
    const xDomain = [xMinValue, xMaxValue];
    const xScale = xLogScale 
      ? d3.scaleLog().domain([Math.pow(10, xMinValue), Math.pow(10, xMaxValue)])
      : d3.scaleLinear().domain(xDomain);
    xScale.range([0, width]);
    
    const yScale = yLogScale
      ? d3.scaleLog().domain([Math.max(0.001, yMinValue), yMaxValue])
      : d3.scaleLinear().domain([yMinValue, yMaxValue]);
    yScale.range([height, 0]);
    
    // 添加X轴
    const xAxis = d3.axisBottom(xScale);
    
    if (xLogScale) {
      xAxis.tickFormat((d: any) => {
        const log = Math.log10(d);
        return log === Math.floor(log) ? `10^${log}` : '';
      });
    }
    
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);
    
    // 添加X轴标签
    const xLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width/2}, ${margin.top + height + 40})`);
    
    xLabelContainer.append("text")
      .attr("text-anchor", "middle")
      .style("font-size", "12px")
      .html(xAxisLabel);
    
    // 添加Y轴
    const yAxis = d3.axisLeft(yScale);
    
    if (yLogScale) {
      yAxis.tickFormat((d: any) => {
        const log = Math.log10(d);
        return log === Math.floor(log) ? `10^${log}` : '';
      });
    }
    
    svg.append("g")
      .call(yAxis);
    
    // 添加Y轴标签
    svg.append("g")
      .attr("transform", `translate(-40, ${height/2})`)
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "middle")
      .style("font-size", "12px")
      .html(yAxisLabel);
    
    // 添加图例
    const legend = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width + 30}, ${margin.top})`);
    
    // 辅助函数：获取系列的颜色
    const getSeriesColor = (series: string) => {
      return seriesInfo[series]?.color || "#000000";
    };
    
    // 辅助函数：获取系列的形状
    const getSeriesShape = (series: string) => {
      return seriesInfo[series]?.shape || "circle";
    };

    // 记录累计高度，以便垂直排列图例项
    let cumulativeHeight = 0;
    
    seriesList.forEach((series) => {
      const seriesData = plotData.filter(d => d.series === series);
      if (seriesData.length === 0) return;
      
      const color = getSeriesColor(series);
      const shapeType = shapeMap[getSeriesShape(series)] || d3.symbolCircle;
      const itemHeight = seriesInfo[series]?.description ? 35 : 20;
      const isVisible = seriesVisibility[series] !== false;
      
      // 图例项容器
      const legendItem = legend.append("g")
        .attr("class", "legend-item")
        .attr("data-series", series)
        .attr("transform", `translate(0, ${cumulativeHeight})`)
        .style("cursor", "pointer")
        .on("click", () => {
          if (onSeriesClick) {
            onSeriesClick(series);
          }
        });
      
      // 添加可见性切换图标
      legendItem.append("text")
        .attr("class", "visibility-toggle-icon")
        .attr("x", -15)
        .attr("y", 14)
        .style("cursor", "pointer")
        .style("font-size", "14px")
        .text(isVisible ? '○' : '●')
        .on('click', function(event) {
          event.stopPropagation();
          if (onVisibilityToggle) {
            onVisibilityToggle(series);
          }
        });
      
      // 绘制图例标记
      const symbol = d3.symbol().size(40).type(shapeType);
      legendItem.append("path")
        .attr("d", symbol())
        .attr("transform", "translate(5, 10)")
        .style("fill", color)
        .style("stroke", "white")
        .style("stroke-width", 1);
      
      // 图例文本 - 系列名称
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 14)
        .attr("text-anchor", "start")
        .style("font-weight", "bold")
        .style("font-size", "12px")
        .style("fill", "#333")
        .html(seriesInfo[series]?.name || series);
      
      // 如果有描述，则添加描述文本
      if (seriesInfo[series]?.description) {
        legendItem.append("text")
          .attr("x", 15)
          .attr("y", 32)
          .attr("text-anchor", "start")
          .style("font-size", "10px")
          .style("fill", "#666")
          .html(seriesInfo[series]?.description || '');
      }
      
      // 更新累计高度
      cumulativeHeight += itemHeight + 5; // 5px作为图例项之间的间隔
    });

  }, [data, seriesList, seriesInfo, seriesVisibility, xAxisLabel, yAxisLabel, xLogScale, yLogScale, xMinValue, xMaxValue, yMinValue, yMaxValue, onSeriesClick, onVisibilityToggle]);

  return <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default BaseChart;